/* eslint-disable arrow-body-style */
// Sentry subscription canceled. Keep direct captures disabled.
// import * as Sentry from "@sentry/react";
import { getCookie, setUniqueIdCookie } from "@src/utils/cookie";
import axios, { AxiosInstance } from "axios";
import moment from "moment";
import { UserInfo } from "../types/userInfo";
import { sendSlackError } from "./error";
import { CheckProfile } from "./types";

export const apiServiceV2: AxiosInstance = axios.create({
	withCredentials: true,
	baseURL: process.env.NEXT_PUBLIC_API_V2_URL,
	timeout: 60000,
	headers: {
		post: {
			"Content-Type": "application/json",
		},
	},
});

// Request interceptors
apiServiceV2.interceptors.request.use(
	async (config) => {
		if (config) {
			const accessToken =
				window.sessionStorage.getItem("accessToken") ||
				window.localStorage.getItem("accessToken") ||
				"";
			if (isTokenExpired(accessToken)) {
				await getNewToken();
			}

			const newAccessToken =
				window.sessionStorage.getItem("accessToken") ||
				window.localStorage.getItem("accessToken") ||
				"";

			config!.headers!["Authorization"] = `Bearer ${newAccessToken}`;
		}
		return config;
	},
	(error) => Promise.reject(error),
);

// Response interceptors
apiServiceV2.interceptors.response.use(
	(response) => {
		return response;
	},
	(error) => {
		if (axios.isAxiosError(error)) {
			if (
				(error.response?.status === 403 &&
					error.response?.data?.message === "허가 되지 않은 API") ||
				error.message === "Network Error" ||
				error.response?.status.toString().startsWith("5")
			) {
				// console.log(error);
				// Sentry.captureException(error);
				sendZapierError(error);
			}
		}
		return Promise.reject({
			...error,
			errorMessage: error?.response?.data?.message || error.message,
		});
	},
);

async function sendZapierError(error: any) {
	const { password, ...errorData } = error;
	await axios.post(`/api/error`, {
		error: errorData,
	});
}

function parseJwt(token: string) {
	try {
		const base64Url = token.split(".")[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			window
				.atob(base64)
				.split("")
				.map(function (c) {
					return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join(""),
		);

		return JSON.parse(jsonPayload);
	} catch (error) {
		return {
			exp: 0,
		};
	}
}

export const getNewToken = async () => {
	try {
		const localRefreshToken = window.localStorage.getItem("refreshToken");
		if (!localRefreshToken) return;
		const response = await axios.post(`${process.env.NEXT_PUBLIC_API_V2_URL}/auth/refresh`, {
			token: localRefreshToken,
		});
		const { accessToken } = response.data;
		if (!accessToken) await logout();
		window.localStorage.setItem("accessToken", accessToken);
	} catch (error) {
		await logoutByRefreshError();
	}
};

export const autoLogin = async () => {
	const response = await apiServiceV2.post(`/auth`);
	return response.data;
};

export const logout = async () => {
	try {
		await axios.post(`${process.env.NEXT_PUBLIC_API_V2_URL}/auth/logout`, {
			token: window.localStorage.getItem("refreshToken"),
		});
	} catch (error) {
		//
	}
	window.localStorage.removeItem("accessToken");
	window.localStorage.removeItem("refreshToken");
};

// refresh에서 로직이 터져서 로그아웃되는 경우
export const logoutByRefreshError = async () => {
	try {
		await axios.post(`${process.env.NEXT_PUBLIC_API_V2_URL}/auth/logout/refresh`, {
			token: window.localStorage.getItem("refreshToken"),
		});
	} catch (error) {
		//
	}
	window.localStorage.removeItem("accessToken");
	window.localStorage.removeItem("refreshToken");
};

export const isTokenExpired = (token: string): boolean => {
	if (!token) return true;
	try {
		return moment().add(1, "minutes").toDate().getTime() >= parseJwt(token).exp * 1000;
	} catch (error) {
		return true;
	}
};

export const checkExistingUser = async (): Promise<CheckProfile | null> => {
	try {
		const response = await apiServiceV2.post<CheckProfile>("auth/check/exist");
		return response.data;
	} catch (error) {
		return null;
	}
};

const clientPixelId = "907870226879067";
const expertPixelId = "1341125860039603";

const pixelIdSet = {
	client: clientPixelId,
	expert: expertPixelId,
};

const eventName = {
	signup: "CompleteRegistration",
	submit: "SubmitApplication",
	start: "StartTrial",
};

export interface MetaConversionApiData {
	userType: keyof typeof pixelIdSet;
	eventName: keyof typeof eventName;
	email: string;
	phone: string;
	url: string;
	uuid: string;
}

export const sendMetaToConversion = async (data: MetaConversionApiData) => {
	setUniqueIdCookie();
	const metaAdInfo = {
		...data,
		fbc: getCookie("_fbc") || "",
		fbp: getCookie("_fbp") || "",
		externalId: getCookie("1id") || "",
	};
	try {
		await apiServiceV2.post("/utils/meta-conversion-api", metaAdInfo);
	} catch (error) {
		// console.log(error);
	}
};

export const sendFailMessageAboutAdsfailToSlack = async (
	phone: string,
	id: string,
	type: string,
) => {
	try {
		await apiServiceV2.post("/utils/ads-fail", {
			phone,
			id,
			type,
		});
	} catch (error) {
		// console.log(error);
	}
};

interface DirectExpertsData {
	expertUuid: string;
	nickname: string;
	job: string;
	year: number;
	openPrice: number;
	skills: string;
	keyword: string;
}

export const getDirectExpertsList = async () => {
	try {
		return (await apiServiceV2.get(`directs/experts/list`)).data as DirectExpertsData[];
	} catch (error) {
		return [];
		// console.log(error);
	}
};

interface MatchDirectUserData {
	company: string;
	name: string;
	email: string;
	phone: string;
}

export interface MatchDirectRequest extends MatchDirectUserData {
	projectName?: string;
	category?: string;
	targetLevelOfExpert?: string;
	targetWorkHour?: string;
	targetDuration?: string;
	boolRemote?: string;
	targetPrice?: number;
	workRequest?: string;
	clientNote?: string;
	matchDirectUuid: number;
}

export const matchDirectClientList = async (): Promise<MatchDirectRequest[]> => {
	try {
		return (await apiServiceV2.get(`/directs/clients/list`)).data as MatchDirectRequest[];
	} catch (error) {
		return [];
	}
};

export const getExpertsDirectStatus = async (): Promise<{
	openPrice: number;
	boolAgreeDirect: number | boolean;
} | null> => {
	try {
		const result = await apiServiceV2.get(`/directs/experts/status`);
		return result.data;
	} catch (error) {
		return null;
	}
};

export interface JdData {
	고객사이름: string;
	브랜드: string;
	비즈니스유형: string;
	산업군: string;
	업무분야: string;
	업무목적: string;
	업무유형: string;
	요청업무: string;
	매칭조건: string;
	필요스킬: string;
	근무형태: string;
	고객사예산: string;
	주예상업무시간: string;
	매칭코멘트: string;
}

export interface JdDataAiResponseResult {
	isConversationComplete: boolean;
	jd: JdData;
	nextMessage: string;
}

export interface JdDataAiResponseResultV2 {
	result: JdDataAiResponseResult | null;
	threadId: string;
}

export interface JdDataAiResponseResultV3 {
	result: JdDataAiResponseResult | null;
	threadId: string;
}

export interface ChatMessageItem {
	role: string;
	content: Content[];
	id?: string;
	type?: string;
	status?: string;
}
interface Content {
	type: string;
	text: string;
	annotations?: any[];
	logprobs?: any[];
}

export interface LeadWithAiRequest {
	userInfo: UserInfo;
	matchUuid: string;
	chatHistory?: ChatMessageItem[];
	isAiLead?: boolean;
}

export interface LeadWithAiResponse {
	isSuccess: boolean;
	isExistingUser?: boolean;
	matchUuid?: string;
    registerUrl?: string;
}

export const leadWithAi = async (data: LeadWithAiRequest): Promise<LeadWithAiResponse | null> => {
	try {
		const result = await apiServiceV2.post(`/match/lead-with-ai`, data);
		return result.data;
	} catch (error) {
		sendSlackError({
			message: (error as any)?.message + JSON.stringify(data),
			title: "submit lead with ai",
		});
		return null;
	}
};

export const chatConsult = async (
	question: string,
	chatHistory: ChatMessageItem[],
	matchUuid: string,
	userInfo: UserInfo,
): Promise<ChatMessageItem[] | null> => {
	try {
		const result = (
			await apiServiceV2.post(
				`/chat-consult-v3`,
				{
					question,
					chatHistory,
					matchUuid,
					userInfo,
				},
				{ timeout: 180000 },
			)
		).data;
		return result?.filter((item: any) => item.role === "assistant");
	} catch (error) {
		sendSlackError({
			message: (error as any)?.message + JSON.stringify(chatHistory),
			title: "chat consult",
		});
		return null;
	}
};

export const checkUserTypeByEmail = async (email: string): Promise<{ isExpert: boolean; message: string }> => {
	try {
		const response = await apiServiceV2.post(`/check-user-type-by-email`, {
			email,
		});
		return response.data;
	} catch (error) {
		sendSlackError({
			message: (error as any)?.message + JSON.stringify({ email }),
			title: "check user type by email",
		});
		return { isExpert: false, message: "이메일 확인 중 오류가 발생했습니다." };
	}
};

export const saveUserInfoOnly = async (
	matchUuid: string,
	userInfo: UserInfo,
): Promise<boolean | null> => {
	try {
		await apiServiceV2.post(`/save-user-info-only`, {
			matchUuid,
			userInfo,
		});
		return true;
	} catch (error) {
		sendSlackError({
			message: (error as any)?.message + JSON.stringify({ matchUuid, userInfo }),
			title: "save user info only",
		});
		return null;
	}
};
